import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { Button } from '../../components/ui/Button.jsx';
import { Modal } from '../../components/ui/Modal.jsx';
import { useTranslation } from '../../i18n/useTranslation.js';
import { inventoryApi } from '../../services/inventoryApi.js';

const EMPTY = { code: '', name: '', email: '', phone: '', address: '' };

export function SupplierCreateModal({ open, onClose }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [form, setForm] = useState(EMPTY);
  const [error, setError] = useState(null);

  const create = useMutation({
    mutationFn: (body) => inventoryApi.createSupplier(body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['inventory', 'suppliers'] });
      setForm(EMPTY);
      setError(null);
      onClose();
    },
    onError: (err) => setError(err?.message || t('common.loadError')),
  });

  const handle = (key) => (e) => setForm((p) => ({ ...p, [key]: e.target.value }));

  function submit(e) {
    e.preventDefault();
    setError(null);
    create.mutate({
      code: form.code.trim(),
      name: form.name.trim(),
      email: form.email.trim() || undefined,
      phone: form.phone.trim() || undefined,
      address: form.address.trim() || undefined,
    });
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t('inv.suppliers.modal.title')}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>{t('common.cancel')}</Button>
          <Button variant="primary" onClick={submit} disabled={create.isPending}>
            {create.isPending ? `${t('common.save')}…` : t('common.create')}
          </Button>
        </>
      }
    >
      <form onSubmit={submit} style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
        <div className="field-row">
          <label className="field">
            <span className="field-label">{t('col.code')}</span>
            <input className="input" value={form.code} onChange={handle('code')} required />
          </label>
          <label className="field">
            <span className="field-label">{t('col.name')}</span>
            <input className="input" value={form.name} onChange={handle('name')} required />
          </label>
        </div>
        <div className="field-row">
          <label className="field">
            <span className="field-label">{t('col.email')}</span>
            <input className="input" type="email" value={form.email} onChange={handle('email')} />
          </label>
          <label className="field">
            <span className="field-label">{t('inv.suppliers.modal.phone')}</span>
            <input className="input" value={form.phone} onChange={handle('phone')} />
          </label>
        </div>
        <label className="field">
          <span className="field-label">{t('inv.suppliers.modal.address')}</span>
          <input className="input" value={form.address} onChange={handle('address')} />
        </label>
        {error && <div className="alert danger">{error}</div>}
      </form>
    </Modal>
  );
}
